import { useMemo } from 'react';
import { RuleStatus, useListRulesQuery } from '../types';

export type RuleSource = {
  source: string;
  rulesCount: number;
  lastUpdated: string;
  status: 'active' | 'inactive';
  hasPublishedRules: boolean;
};

export type UseSourcesReturn = {
  sources: RuleSource[];
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
};

// Dynamically aggregates rules by source for the Rules dashboard
export const useSources = (): UseSourcesReturn => {
  const { data, loading, error, refetch } = useListRulesQuery({
    variables: {
      listRulesInput: {},
    },
  });

  const sources = useMemo<RuleSource[]>(() => {
    const rules = data?.listRules?.rules ?? [];
    if (rules.length === 0) return [];

    const sourceMap = new Map<
      string,
      {
        rulesCount: number;
        lastUpdated: string;
        hasPublishedRules: boolean;
      }
    >();

    rules.forEach(rule => {
      const sourceKey = rule.source || 'Unknown';
      const updatedAt = rule.updatedAt || new Date().toISOString();
      const isPublished = rule.status === RuleStatus.Published;

      if (!sourceMap.has(sourceKey)) {
        sourceMap.set(sourceKey, {
          rulesCount: 0,
          lastUpdated: updatedAt,
          hasPublishedRules: isPublished,
        });
      }

      const aggregate = sourceMap.get(sourceKey);
      if (!aggregate) return;

      aggregate.rulesCount += 1;
      aggregate.hasPublishedRules = aggregate.hasPublishedRules || isPublished;

      if (new Date(updatedAt) > new Date(aggregate.lastUpdated)) {
        aggregate.lastUpdated = updatedAt;
      }
    });

    return Array.from(sourceMap.entries())
      .map(([source, agg]) => {
        const status: RuleSource['status'] = agg.hasPublishedRules
          ? 'active'
          : 'inactive';
        return {
          source,
          rulesCount: agg.rulesCount,
          lastUpdated: agg.lastUpdated,
          hasPublishedRules: agg.hasPublishedRules,
          status,
        };
      })
      .sort((a, b) => {
        // Active first, then most recently updated
        if (a.status !== b.status) {
          return a.status === 'active' ? -1 : 1;
        }
        return (
          new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
        );
      });
  }, [data]);

  return { sources, loading, error, refetch };
};
